import { api } from '@common';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Tier discounts
const TIER_DISCOUNTS: Record<string, number> = {
  tier1: 0.12,
  tier2: 0.09,
  tier3: 0.05,
  retail: 0,
};

export const calculatePriceWithTier = (basePrice: number, tier: string): number => {
  const discount = TIER_DISCOUNTS[tier] || 0;
  return Number((basePrice * (1 - discount)).toFixed(2));
};

export const getTierDiscountPercent = (tier: string): number => {
  return (TIER_DISCOUNTS[tier] || 0) * 100;
};

export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  size: string;
  color: string;
  price: number;
  stockQuantity: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductDocument {
  id: string;
  productId: string;
  name: string;
  description?: string;
  fileUrl: string;
  createdAt: Date;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  images?: string[];
  videoUrl?: string;
  sizes?: string;
  category?: string;
  stockQuantity: number;
  colors?: string[];
  stripeProductId?: string;
  stripePriceId?: string;
  variants?: ProductVariant[];
  documents?: ProductDocument[];
  relatedProducts?: Product[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductsResponse {
  data: Product[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

export interface CreateProductInput {
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  images?: string[];
  videoUrl?: string;
  sizes?: string;
  category?: string;
  stockQuantity?: number;
  colors?: string[];
  variants?: {
    size: string;
    color: string;
    price: number;
    stockQuantity?: number;
  }[];
  documentsMetadata?: {
    name: string;
    description?: string;
  }[];
}

export const useProducts = (params?: ProductQueryParams) => {
  const queryClient = useQueryClient();

  // Fetch all products with pagination and filters
  const products = useQuery<ProductsResponse>({
    queryKey: ['products', params],
    queryFn: async () => {
      try {
        const response = await api.get('/products', { params });
        return response.data;
      } catch (error) {
        console.error('Error fetching products:', error);
        message.error('Error loading products');
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch a single product by ID
  const getProduct = (productId: string) => {
    return useQuery<Product>({
      queryKey: ['product', productId],
      queryFn: async () => {
        try {
          const response = await api.get(`/products/${productId}`);
          return response.data;
        } catch (error) {
          console.error(`Error fetching product ${productId}:`, error);
          message.error('Error loading product details');
          throw error;
        }
      },
      enabled: !!productId,
    });
  };

  // Create a new product
  const createProduct = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post('/products', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      message.success('Product created successfully');
    },
    onError: (error: any) => {
      console.error('Error creating product:', error);
      message.error(error.response?.data?.message || 'Error creating product');
    },
  });

  // Update a product
  const updateProduct = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormData }) => {
      const response = await api.patch(`/products/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      message.success('Product updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating product:', error);
      message.error(error.response?.data?.message || 'Error updating product');
    },
  });

  // Delete a product
  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      message.success('Product deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting product:', error);
      message.error(error.response?.data?.message || 'Error deleting product');
    },
  });

  // Check if a variant can be deleted
  const canDeleteVariant = async (variantId: string): Promise<{ canDelete: boolean; reason?: string; orderCount?: number }> => {
    try {
      const response = await api.get(`/products/variants/${variantId}/can-delete`);
      return response.data;
    } catch (error) {
      console.error('Error checking if variant can be deleted:', error);
      throw error;
    }
  };

  return {
    products,
    getProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    canDeleteVariant,
  };
};

